import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import type { ReactElement } from "react";
import { toastr } from "react-redux-toastr";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isLoggedInSelector,
  isPasswordChangeRequiredSelector,
} from "@/features/auth/store/authSlice";
import {
  loggedInUserRoleSelector,
  loggedInUserSelector,
  setUser,
} from "@/features/profile/store/userSlice";
import { selectedBrandSelector } from "@/shared/store/brandSlice";
import ProtectedRoute from "../ProtectedRoute";
import { resetHasLoggedOut } from "@/features/auth/store/authSlice";
import { getSettingsThunk } from "@/shared/store/settingsSlice";
import { loginWithToken } from "@/shared/helpers/tokenBasedLoginHelper";
import { rpGTM } from "@/config/rpGTM";
import {
  BRAND_CONST,
  USER_ROLE,
  isBrandSubUserAllowed,
} from "@/config/constants";
import { ROLES } from "./rolesConfig";
import { ROUTES, ROUTE_SEGMENTS } from "./routePaths";
import CommonSockets from "@/shared/components/socketComponents/CommonSockets";
import CommonSSE from "@/shared/components/sseComponents/CommonSSE";
import ScrollTo from "@/components/ScrollTo";
import LogoutWhenMaintenanceOn from "@/components/utils/LogoutWhenMaintenanceOn";
import PendingInvoiceCheck from "@/shared/components/stripe/PendingInvoiceCheck";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import UserManagementTermsGate from "@/features/user-management/components/UserManagementTermsGate";
import { userManagementService } from "@/features/user-management/services/userManagementService";
import { useUrlSelectedBrand } from "@/shared/hooks/useUrlSelectedBrand";

// ============================================================================
// IMPORTED COMPONENTS
// ============================================================================

// Auth Components
import {
  LoginPage,
  LoginRedirectPage,
  LogoutCallbackPage,
  SignupPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ValidateSignupLinkPage,
  CreatePasswordPage,
} from "@/features/auth";
import NotFound404 from "@/components/NotFound404";
import { SelfSubscriptionPage } from "@/features/subscriptions/self-subscription";
import Home from "@/shared/pages/HomePage";

// Layout Components
import OverviewDashboardLayout from "@/components/layouts/dashboard/OverviewDashboardLayout";

// Subscription Components
import { LocationSubscriptionPage } from "@/features/subscriptions/new-subscription";
import { ManageActiveSubscriptionPage } from "@/features/subscriptions/manage-active-subscription";
import { SubscriptionInvoicePage } from "@/features/subscriptions/subscription-invoices";
import { MSLocationSubscriptionPage } from "@/features/morgan-stanley";

// Resource & Education
import { EducationCenterPage } from "@/features/education-center";
import { MSActivationPage } from "@/features/morgan-stanley";

// Campaign Management
import { CreateCampaignPage } from "@/features/campaigns/create-campaign";
import { DigitalCampaignListPage } from "@/features/campaigns/digital/digital-campaign-list";
import { CampaignOverviewPage } from "@/features/campaigns/campaign-overview";
import { DirectMailCampaignListPage } from "@/features/campaigns/direct-mail/direct-mail-campaign-list";
import { BoostCampaignPage } from "@/features/campaigns/digital/boost-campaign";
import { CampaignPausePage } from "@/features/campaigns/digital/pause-campaign";
import { ExtendCampaignPage } from "@/features/campaigns/digital/extend-campaign";

// Email Campaigns
import {
  EmailCampaignsPage,
  EmailCampaignReportPage,
  EmailCampaignJoinPage,
  EmailCampaignReportDetailPage,
} from "@/features/campaigns/email";

// Reports
import { JoinedDigitalCampaignListPage } from "@/features/campaigns/digital/joined-digital-campaign-list";
import { JoinedDirectMailCampaignListPage } from "@/features/campaigns/direct-mail/joined-direct-mail-campaign-list";
import { BrandDigitalCampaignListPage } from "@/features/campaigns/digital/brand-digital-campaign-list";
import { BrandDirectMailCampaignListPage } from "@/features/campaigns/direct-mail/brand-direct-mail-campaign-list";
import { CampaignReportPage } from "@/features/campaigns/digital/campaign-report";
import { DirectMailCampaignReportPage } from "@/features/campaigns/direct-mail/direct-mail-campaign-report";
import { LocationReportPage } from "@/features/locations/location-report";
import { LocationListPage } from "@/features/locations/location-list";
import { LocationManagementPage } from "@/features/locations/location-management";
import { UserManagementPage } from "@/features/user-management";

// Payment
import { PaymentReturnPage } from "@/features/payments/payment-return";
import { PaymentsPage } from "@/features/payments/payment-history";

// Other Pages
import { BrandRequestPage } from "@/features/brand-request";
import { LearnMorePage } from "@/features/campaigns/learn-more";

// Dashboard Components
import {
  BrandDashboardPage,
  ParticipantDashboardPage,
} from "@/features/dashboard";
import { ProfilePage } from "@/features/profile";
import ThankYouPage from "@/shared/pages/ThankYouPage";
import { LocationSubscriptionsPage } from "@/features/locations/location-subscriptions";

// Participant Components
import {
  LaunchDigitalCampaignPage,
  JoinCampaignConfirmationPage,
} from "@/features/campaigns/digital/launch-digital-campaign";
import { JoinMailSharkCampaignConfirmPage as PostcardCampaignConfirmation } from "@/features/campaigns/direct-mail";
import {
  DirectMailJoinCampaignPage,
  DirectMailRRDConfirmationPage,
} from "@/features/campaigns/direct-mail";
import {
  NationalPlusUpConfirmationPage,
  NationalPlusUpJoinCampaignPage,
} from "@/features/campaigns/direct-mail/national-plus-up";

// ============================================================================
// ROLE CONSTANTS
// ============================================================================
// Predefined role groups for easy route protection
// Usage: protectedRoute("path", <Component />, ROLES.PARTICIPANT)

// ============================================================================
// ROUTE UTILITIES
// ============================================================================
// Helper functions to create routes easily

const protect = (
  element: ReactElement,
  roles: string[],
  requireBrand = true
) => (
  <ProtectedRoute element={element} roles={roles} requireBrand={requireBrand} />
);

const publicRoute = (path: string, element: ReactElement) => ({
  path,
  element,
});

const protectedRoute = (
  path: string,
  element: ReactElement,
  roles: string[],
  children?: any[]
) => ({
  path,
  element: protect(element, roles),
  ...(children && { children }),
});

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

const RootLayout = () => {
  const location = useLocation();
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  const selectedBrand = useSelector(selectedBrandSelector);
  const loggedInUser = useSelector(loggedInUserSelector);
  const isPasswordChangeRequired = useSelector(
    isPasswordChangeRequiredSelector
  );
  const dispatch = useDispatch<any>();
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const shouldRenderAuthenticatedShell =
    location.pathname !== ROUTES.LOGOUT_CALLBACK;
  useUrlSelectedBrand();

  const isTermsAccepted = useMemo(() => {
    if (loggedInUser?.is_terms_accepted === null) return false;

    return (
      loggedInUser?.is_terms_accepted === true ||
      loggedInUser?.is_terms_accepted === 1 ||
      loggedInUser?.is_terms_accepted === "1" ||
      loggedInUser?.is_terms_accepted === "true"
    );
  }, [loggedInUser?.is_terms_accepted]);

  const shouldShowTermsGate =
    Boolean(loggedInUserRole) &&
    loggedInUser?.id &&
    !loggedInUser?.is_master_pass &&
    !isPasswordChangeRequired &&
    !isTermsAccepted;

  const handleAcceptTerms = useCallback(async () => {
    try {
      setIsAcceptingTerms(true);
      await userManagementService.acceptTerms();
      dispatch(setUser({ ...loggedInUser, is_terms_accepted: 1 }));
      toastr.success("Success", "Terms and conditions accepted successfully");
    } finally {
      setIsAcceptingTerms(false);
    }
  }, [dispatch, loggedInUser]);

  useEffect(() => {
    if (shouldRenderAuthenticatedShell && selectedBrand?.id) {
      dispatch(resetHasLoggedOut());
      loginWithToken({ data: selectedBrand });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, shouldRenderAuthenticatedShell]);

  useEffect(() => {
    if (shouldRenderAuthenticatedShell && selectedBrand?.id) {
      dispatch(getSettingsThunk(selectedBrand?.id));
    }
  }, [selectedBrand?.id, dispatch, shouldRenderAuthenticatedShell]);

  useEffect(() => {
    if (shouldRenderAuthenticatedShell && loggedInUser?.id) {
      rpGTM.setUserDetails({
        userId: loggedInUser.id,
        userEmail: loggedInUser.email,
        userRole: loggedInUser?.is_master_pass
          ? "Admin"
          : loggedInUser?.role?.name || loggedInUserRole,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUserRole, loggedInUser?.id, shouldRenderAuthenticatedShell]);

  return (
    <>
      <Outlet />
      {shouldRenderAuthenticatedShell && (
        <>
          <UserManagementTermsGate
            open={Boolean(shouldShowTermsGate)}
            onAccept={handleAcceptTerms}
            isLoading={isAcceptingTerms}
          />
          <CommonSockets />
          <CommonSSE />
          <ScrollTo />
          <LogoutWhenMaintenanceOn />
          <PendingInvoiceCheck />
        </>
      )}
    </>
  );
};

// ============================================================================
// CONDITIONAL ROUTE COMPONENTS
// ============================================================================
// Components that handle conditional logic (redirects, role-based rendering)

const RootRoute = () => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  const selectedBrand = useSelector(selectedBrandSelector);
  if (isLoggedIn && !loggedInUserRole)
    return <FullScreenLoader isActive whiteBg hideIcon />;
  const shouldRedirectToHome =
    isLoggedIn &&
    ROLES.PARTICIPANT_PARENT.includes(loggedInUserRole) &&
    !selectedBrand;
  return shouldRedirectToHome ? (
    <Navigate to={ROUTES.HOME} replace />
  ) : (
    <LoginPage />
  );
};

const HomeRoute = () => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  const selectedBrand = useSelector(selectedBrandSelector);
  const isPasswordChangeRequired = useSelector(
    isPasswordChangeRequiredSelector
  );

  if (!isLoggedIn) return <Navigate to={ROUTES.ROOT} replace />;
  if (!loggedInUserRole) return <FullScreenLoader isActive whiteBg hideIcon />;
  if (isPasswordChangeRequired) return <Home />;
  if (!ROLES.PARTICIPANT_PARENT.includes(loggedInUserRole)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  if (selectedBrand?.id) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return <Home />;
};

const DashboardIndex = () => {
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  return ROLES.PARTICIPANT.includes(loggedInUserRole) ? (
    <ParticipantDashboardPage />
  ) : (
    <BrandDashboardPage />
  );
};

const LocationReportRoute = () => {
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  return !ROLES.PARTICIPANT.includes(loggedInUserRole) ? (
    <LocationReportPage />
  ) : (
    <Navigate to={ROUTES.NOT_FOUND} replace />
  );
};

const SubscriptionPackagesRoute = () => {
  const selectedBrand = useSelector(selectedBrandSelector);
  return selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ? (
    <MSLocationSubscriptionPage />
  ) : (
    <LocationSubscriptionPage />
  );
};

const UserManagementRoute = () => {
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  const loggedInUser = useSelector(loggedInUserSelector);
  const selectedBrand = useSelector(selectedBrandSelector);
  const canAccessUserManagement =
    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
    !loggedInUser?.is_sub_participant &&
    isBrandSubUserAllowed(selectedBrand);

  if (!canAccessUserManagement) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <UserManagementPage />;
};

const createReportRoute = (ReportsList, ReportsPage) => () => {
  const loggedInUserRole = useSelector(loggedInUserRoleSelector);
  const isParticipant = ROLES.PARTICIPANT.includes(loggedInUserRole);
  return protect(
    isParticipant ? <ReportsList /> : <ReportsPage />,
    isParticipant ? ROLES.PARTICIPANT : ROLES.ADMIN
  );
};

const CampaignReportRoute = createReportRoute(
  JoinedDigitalCampaignListPage,
  BrandDigitalCampaignListPage
);
const PostcardReportRoute = createReportRoute(
  JoinedDirectMailCampaignListPage,
  BrandDirectMailCampaignListPage
);

// ============================================================================
// ROUTE DEFINITIONS
// ============================================================================

// ----------------------------------------------------------------------------
// PUBLIC ROUTES
// ----------------------------------------------------------------------------
// These routes are accessible without authentication
// To add a new public route: publicRoute("path", <YourComponent />)

const publicRoutes = [
  publicRoute("/", <RootRoute />),
  publicRoute(ROUTES.LOGIN_REDIRECT, <LoginRedirectPage />),
  publicRoute(ROUTES.LOGOUT_CALLBACK, <LogoutCallbackPage />),
  publicRoute(ROUTES.LOGIN, <LoginPage />),
  publicRoute(ROUTES.SIGNUP, <SignupPage />),
  publicRoute(ROUTES.FORGOT_PASSWORD, <ForgotPasswordPage />),
  publicRoute(ROUTES.PASSWORD_RESET, <ResetPasswordPage />),
  publicRoute(ROUTES.VALIDATE_SIGNUP_LINK, <ValidateSignupLinkPage />),
  publicRoute(ROUTES.CREATE_PASSWORD, <CreatePasswordPage />),
  publicRoute(ROUTES.SELF_SUBSCRIBE, <SelfSubscriptionPage />),
  publicRoute(ROUTES.HOME, <HomeRoute />),
];

// ----------------------------------------------------------------------------
// PARTICIPANT NESTED ROUTES
// ----------------------------------------------------------------------------
// Routes nested under /participant layout
// These are child routes that render inside ParticipantLayout

const participantRoutes = [
  { index: true, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.JOIN_CAMPAIGN,
    element: <LaunchDigitalCampaignPage />,
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.JOIN_POSTCARD_CAMPAIGN,
    element: <DirectMailJoinCampaignPage />,
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.JOIN_NATIONAL_PLUS_UP_CAMPAIGN,
    element: <NationalPlusUpJoinCampaignPage />,
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.CONFIRM,
    element: protect(<JoinCampaignConfirmationPage />, ROLES.PARTICIPANT),
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.POSTCARD_CONFIRM,
    element: protect(<PostcardCampaignConfirmation />, ROLES.PARTICIPANT),
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.RRD_JOIN_CAMPAIGN_CONFIRM,
    element: protect(<DirectMailRRDConfirmationPage />, ROLES.PARTICIPANT),
  },
  {
    path: ROUTE_SEGMENTS.PARTICIPANT.NATIONAL_PLUS_UP_CONFIRM,
    element: protect(<NationalPlusUpConfirmationPage />, ROLES.PARTICIPANT),
  },
  { path: "*", element: <Navigate to={ROUTES.DASHBOARD} replace /> },
];

// ----------------------------------------------------------------------------
// DASHBOARD NESTED ROUTES
// ----------------------------------------------------------------------------
// Routes nested under /dashboard layout
// These are child routes that render inside DashboardLayout

const dashboardRoutes = [
  { index: true, element: <DashboardIndex /> },
  { path: ROUTE_SEGMENTS.DASHBOARD.PROFILE, element: <ProfilePage /> },
  {
    path: ROUTE_SEGMENTS.DASHBOARD.LOCATION_REPORT,
    element: <LocationReportRoute />,
  },
  {
    path: ROUTE_SEGMENTS.DASHBOARD.DIGITAL_CAMPAIGN_REPORT,
    element: <CampaignReportPage />,
  },
  {
    path: ROUTE_SEGMENTS.DASHBOARD.DIRECT_MAIL_REPORT,
    element: <DirectMailCampaignReportPage />,
  },
  { path: ROUTE_SEGMENTS.DASHBOARD.LEARN_MORE, element: <LearnMorePage /> },
  { path: ROUTE_SEGMENTS.DASHBOARD.THANK_YOU, element: <ThankYouPage /> },
  {
    path: ROUTE_SEGMENTS.DASHBOARD.SUBSCRIPTIONS,
    element: <LocationSubscriptionsPage />,
  },
  { path: "*", element: <Navigate to={ROUTES.NOT_FOUND} replace /> },
];

// ----------------------------------------------------------------------------
// PAYMENT RETURN ROUTES
// ----------------------------------------------------------------------------
// Grouped routes for payment return pages
// To add: protectedRoute("new-return", <Component />, ROLES.PAYMENT_RETURN)

const paymentReturnRoutes = [
  protectedRoute(
    ROUTES.SUBSCRIPTION_RETURN,
    <PaymentReturnPage type="subscription" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.SUBSCRIPTION_CANADA_RETURN,
    <PaymentReturnPage type="canada_subscription" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.SUBSCRIPTION_CAD_RETURN,
    <PaymentReturnPage type="cad_subscription" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.SUBSCRIPTION_PAD_RETURN,
    <PaymentReturnPage type="pad_subscription" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.JOIN_CAMPAIGN_RETURN,
    <PaymentReturnPage type="join_campaign" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.RRD_JOIN_CAMPAIGN_RETURN,
    <PaymentReturnPage type="rrd_join_campaign" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.NATIONAL_PLUS_UP_JOIN_CAMPAIGN_RETURN,
    <PaymentReturnPage type="national_plus_up_join_campaign" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.BOOST_CAMPAIGN_RETURN,
    <PaymentReturnPage type="boost_campaign" />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.EXTEND_CAMPAIGN_RETURN,
    <PaymentReturnPage type="extend_campaign" />,
    ROLES.PAYMENT_RETURN
  ),
];

// ----------------------------------------------------------------------------
// PROTECTED ROUTES
// ----------------------------------------------------------------------------
// All routes that require authentication
// To add a new protected route: protectedRoute("path", <Component />, ROLES.ROLE_NAME)
// To add nested routes: protectedRoute("path", <Layout />, ROLES.ROLE_NAME, [childRoutes])

const protectedRoutes = [
  protectedRoute(
    ROUTES.PARTICIPANT,
    <Outlet />,
    ROLES.ALL_AUTHENTICATED,
    participantRoutes
  ),
  protectedRoute(
    ROUTES.DASHBOARD,
    <Outlet />,
    ROLES.ALL_AUTHENTICATED,
    dashboardRoutes
  ),
  protectedRoute(
    ROUTES.OVERVIEW_DASHBOARD,
    <OverviewDashboardLayout />,
    ROLES.PARENT_COMPANY
  ),
  protectedRoute(
    ROUTES.SUBSCRIPTION_PACKAGES,
    <SubscriptionPackagesRoute />,
    ROLES.SUBSCRIPTION
  ),
  protectedRoute(
    ROUTES.MANAGE_SUBSCRIPTION,
    <ManageActiveSubscriptionPage />,
    ROLES.SUBSCRIPTION
  ),
  protectedRoute(
    ROUTES.EDUCATION_CENTER,
    <EducationCenterPage />,
    ROLES.SUBSCRIPTION
  ),
  protectedRoute(
    ROUTES.LAUNCH_CAMPAIGN,
    <DigitalCampaignListPage />,
    ROLES.PARTICIPANT
  ),
  protectedRoute(
    ROUTES.CAMPAIGN_OVERVIEW,
    <CampaignOverviewPage />,
    ROLES.ADMIN
  ),
  protectedRoute(
    ROUTES.LAUNCH_DIRECT_MAIL,
    <DirectMailCampaignListPage />,
    ROLES.PARTICIPANT
  ),
  protectedRoute(
    ROUTES.EMAIL_CAMPAIGNS,
    <EmailCampaignsPage />,
    ROLES.PARTICIPANT_BRAND
  ),
  protectedRoute(
    ROUTES.EMAIL_CAMPAIGN_JOIN,
    <EmailCampaignJoinPage />,
    ROLES.PARTICIPANT_BRAND
  ),
  protectedRoute(
    ROUTES.EMAIL_CAMPAIGN_REPORT_DETAIL,
    <EmailCampaignReportDetailPage />,
    ROLES.PARTICIPANT_BRAND
  ),
  protectedRoute(ROUTES.EMAIL_CAMPAIGN_REPORT, <EmailCampaignReportPage />, [
    ...ROLES.PARTICIPANT_BRAND,
    USER_ROLE.PARENT_COMPANY.NAME,
  ]),
  { path: ROUTES.DIGITAL_CAMPAIGNS, element: <CampaignReportRoute /> },
  { path: ROUTES.DIRECT_MAIL, element: <PostcardReportRoute /> },
  protectedRoute(ROUTES.MORGAN_STANLEY_ACTIVATION, <MSActivationPage />, [
    ...ROLES.PARTICIPANT,
    ...ROLES.BRAND,
  ]),
  protectedRoute(
    ROUTES.LOCATION_MANAGEMENT,
    <LocationManagementPage />,
    ROLES.ADMIN
  ),
  protectedRoute(
    ROUTES.USER_MANAGEMENT,
    <UserManagementRoute />,
    ROLES.PARTICIPANT
  ),
  protectedRoute(ROUTES.LOCATION_REPORTS, <LocationListPage />, ROLES.ADMIN),
  protectedRoute(ROUTES.CREATE_CAMPAIGN, <CreateCampaignPage />, ROLES.BRAND),
  protectedRoute(ROUTES.REQUESTS, <BrandRequestPage />, ROLES.ADMIN),
  protectedRoute(
    ROUTES.ADJUST_BUDGET,
    <BoostCampaignPage />,
    ROLES.PARTICIPANT
  ),
  protectedRoute(
    ROUTES.CAMPAIGN_PAUSE,
    <CampaignPausePage />,
    ROLES.PAYMENT_RETURN
  ),
  protectedRoute(
    ROUTES.SUBSCRIPTION_INVOICE,
    <SubscriptionInvoicePage />,
    ROLES.PARTICIPANT
  ),
  protectedRoute(ROUTES.PAYMENTS, <PaymentsPage />, ROLES.PARTICIPANT),
  protectedRoute(
    ROUTES.EXTEND_CAMPAIGN,
    <ExtendCampaignPage />,
    ROLES.PARTICIPANT
  ),
  ...paymentReturnRoutes,
  publicRoute(ROUTES.NOT_FOUND, <NotFound404 />),
  publicRoute("*", <NotFound404 />),
];

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================

const basePath = "";

export const appRoutes = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [...publicRoutes, ...protectedRoutes],
  },
];

export const router = createBrowserRouter(appRoutes, {
  basename: basePath || undefined,
});
